import { useState } from 'react';
import { Card } from '../components/Painel';
import type { ConfigurationResponse } from '../lib/api';
import { formatInstant } from '../lib/format';
import { FormularioDeMudanca, classeTemFormulario } from './FormularioDeMudanca';

/**
 * Controles e configuracao (Back Office SPEC §14, Wireframes §10).
 *
 * Regras materializadas nesta tela:
 * - nenhum parametro ajustavel aparece sem CLASSE (Master SPEC §8);
 * - valor de chave SECRETA nunca e exibido;
 * - `CONSTITUTIONAL` abre workflow governado de policy, nunca campo inline;
 * - `CAPABILITY` e read-only;
 * - quem nao tem autoridade ve "Solicitar mudança", nao "Alterar".
 */

const CLASS_LABEL: Record<string, string> = {
  constitutional: 'CONSTITUCIONAL',
  hard_limit: 'LIMITE DURO',
  operating_policy: 'POLÍTICA OPERACIONAL',
  learning_allowlisted: 'AJUSTÁVEL POR APRENDIZADO',
  experimental: 'EXPERIMENTAL',
  capability: 'CAPACIDADE (somente leitura)',
};

const CLASS_MEANING: Record<string, string> = {
  constitutional: 'Somente o dono, por fluxo de política e ADR. Aprendizado nunca altera.',
  hard_limit: 'Aprovado pelo dono; os gates aplicam. Aumentar exige autoridade do dono.',
  operating_policy: 'Administrador autorizado altera dentro do escopo dele.',
  learning_allowlisted: 'Aprendizado pode alterar com evidência, monitoramento e rollback.',
  experimental: 'Escopo temporário de experimento.',
  capability: 'Derivada da validação da integração externa. Não se altera por comando.',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'versão ativa',
  fail_closed_default: 'sem aprovação, usando o padrão mais restritivo',
  not_configured: 'não configurado',
};

const CLASS_ORDER = [
  'constitutional',
  'hard_limit',
  'operating_policy',
  'learning_allowlisted',
  'experimental',
  'capability',
];

type Parametro = ConfigurationResponse['parameters'][number];

/**
 * Um RECORTE desta mesma tela.
 *
 * Nasceu da resposta do Ferrari em 17/09/2026 sobre a divergencia entre as 16
 * areas do layout e as 18 da Console SPEC §12: _"o que tiver a mais, bota a
 * mais ai pra gente ter mais informacao"_.
 *
 * Medido antes de construir: das 18 da §12, 14 ja tinham lugar nas 16. Duas das
 * que faltavam ("Tetos & Limites" e "Autonomia") NAO sao telas novas, sao
 * assuntos que ja estao nesta tela, misturados aos demais parametros. Entao o
 * que falta e um CAMINHO ate eles, e nao uma segunda tela.
 *
 * Por que recorte e nao tela propria: uma copia da grade de parametros
 * divergiria na segunda semana, e a que envelhece e sempre a menos usada. Aqui
 * a regra de renderizar parametro existe uma vez, e o recorte so diz QUAIS
 * entram.
 */
export type RecorteDeConfiguracao = {
  readonly id: string;
  readonly titulo: string;
  readonly explicacao: string;
  /** Quais parametros entram. Predicado, e nao lista de chaves escrita a mao. */
  readonly inclui: (p: Parametro) => boolean;
};

/*
  Chaves FECHADAS, e nao `Record<string, ...>`.

  Com `Record<string, ...>` o TypeScript devolve `| undefined` em cada acesso
  (`noUncheckedIndexedAccess`), o que e correto: um indice de string qualquer
  pode nao existir. Mas os recortes sao um conjunto fechado e conhecido, e
  declarar isso deixa `RECORTES.tetos` ser o que ele e, sem `!` espalhado por
  quem chama.
*/
export const RECORTES: {
  readonly tetos: RecorteDeConfiguracao;
  readonly autonomia: RecorteDeConfiguracao;
} = {
  tetos: {
    id: 'tetos',
    titulo: 'Tetos e limites',
    explicacao:
      'Os valores que o sistema não pode ultrapassar. Aumentar qualquer um deles exige autoridade do dono, e os gates aplicam o limite antes de qualquer gasto ou envio.',
    inclui: (p) => p.class === 'hard_limit',
  },
  autonomia: {
    id: 'autonomia',
    titulo: 'Autonomia',
    explicacao:
      'Quanto cada módulo pode decidir sozinho, e a partir de onde precisa de aprovação humana. O padrão de fábrica é o mais conservador: recomendar e esperar decisão.',
    /*
      Predicado por SUFIXO da chave, e nao lista escrita a mao.

      Os tres parametros de hoje sao `community.autonomy_level`,
      `sales.autonomy_level` e `paid_media.autonomy`, ou seja nem o nome e
      uniforme. Uma lista fixa deixaria o quarto de fora em silencio no dia em
      que ele nascesse, e o recorte mostraria menos do que existe sem nada
      reprovar. Um teste de contrato confere este predicado contra o registro
      REAL do servidor.
    */
    inclui: (p) => /\.autonomy(_level)?$/.test(p.parameter_key),
  },
};

export function Controls({
  configuration,
  onRecarregar,
  recorte,
}: {
  configuration: ConfigurationResponse;
  /**
   * Recarrega a projecao depois de uma mudanca aplicada.
   *
   * Opcional porque a tela tambem e usada em teste sem o ciclo de recarga, mas
   * em producao ela SEMPRE vem: "salvar" nao esta pronto sem o caminho de ler
   * de volta, e sem isto a tela mostraria o valor velho depois de aplicar.
   */
  onRecarregar?: () => void;
  /** Sem recorte, a tela e a completa, exatamente como era. */
  recorte?: RecorteDeConfiguracao;
}) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = configuration.parameters.find((p) => p.parameter_key === selectedKey) ?? null;

  const parametros =
    recorte === undefined
      ? configuration.parameters
      : configuration.parameters.filter((p) => recorte.inclui(p));

  const grouped = CLASS_ORDER.map((cls) => ({
    cls,
    items: parametros.filter((p) => p.class === cls),
  })).filter((g) => g.items.length > 0);

  const notConfigured = parametros.filter((p) => p.status !== 'active').length;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">
        {recorte === undefined ? 'Controles e configuração' : recorte.titulo}
      </h1>

      {recorte !== undefined && (
        <p className="max-w-3xl text-sm text-ink-muted">{recorte.explicacao}</p>
      )}

      {/*
        Recorte VAZIO diz que esta vazio, e nao mostra uma tela em branco.

        Um recorte e um item de menu: se o predicado deixar de casar (chave
        renomeada no servidor, classe trocada), a pessoa clica e encontra o
        nada, sem nenhum sinal de que algo quebrou. Aqui ela le que o recorte
        existe e nao encontrou parametro, que e a diferenca entre "nao ha" e
        "nao carregou".
      */}
      {recorte !== undefined && parametros.length === 0 ? (
        <p
          className="max-w-3xl rounded border border-warn-border bg-warn-surface px-3 py-2 text-sm text-warn-ink"
          data-testid="recorte-vazio"
        >
          Nenhum parâmetro deste recorte foi encontrado na configuração atual. Isso não significa
          que o limite não existe: significa que esta tela não conseguiu localizá-lo, e vale avisar
          quem cuida da configuração.
        </p>
      ) : (
        <p className="max-w-3xl rounded border border-line-strong bg-surface px-3 py-2 text-sm">
          <strong>
            {notConfigured} de {parametros.length}
          </strong>{' '}
          parâmetros ainda não têm versão aprovada pelo dono. Os que têm padrão marcado como{' '}
          <em>mais restritivo</em> operam com zero gasto e zero envio; os demais fazem o gate
          correspondente falhar fechado. Nenhum valor foi inventado.
        </p>
      )}

      {grouped.map(({ cls, items }) => (
        <section key={cls} aria-labelledby={`classe-${cls}`}>
          <Card>
            <h2
              id={`classe-${cls}`}
              className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
            >
              {CLASS_LABEL[cls] ?? cls}
            </h2>
            <p className="mt-1 text-xs text-ink-muted">{CLASS_MEANING[cls]}</p>

            <div className="mt-2 overflow-x-auto">
              <table className="w-full min-w-[36rem] border-collapse text-sm">
                <caption className="sr-only">{`Parâmetros da classe ${CLASS_LABEL[cls]}`}</caption>
                <thead>
                  <tr className="border-b border-line text-left">
                    <th scope="col" className="py-1 pr-3 font-semibold">
                      Parâmetro
                    </th>
                    <th scope="col" className="py-1 pr-3 font-semibold">
                      Valor
                    </th>
                    <th scope="col" className="py-1 pr-3 font-semibold">
                      Estado
                    </th>
                    <th scope="col" className="py-1 font-semibold">
                      Ação
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.parameter_key} className="border-b border-line align-top">
                      <th scope="row" className="py-1 pr-3 font-normal">
                        <button
                          type="button"
                          onClick={() => setSelectedKey(p.parameter_key)}
                          className="text-left underline decoration-dotted focus:outline-none focus:ring-2 focus:ring-accent"
                          aria-expanded={selectedKey === p.parameter_key}
                        >
                          {p.display_name}
                        </button>
                        <span className="block text-xs text-ink-faint">{p.parameter_key}</span>
                      </th>
                      <td className="py-1 pr-3">
                        {p.secret ? (
                          // Valor de segredo NUNCA e projetado, nem para o dono.
                          <span className="italic text-ink-faint">valor protegido</span>
                        ) : p.status === 'active' ? (
                          <span>{JSON.stringify(p.value)}</span>
                        ) : p.fail_closed_default !== null &&
                          p.fail_closed_default !== undefined ? (
                          <span className="text-ink-muted">
                            {JSON.stringify(p.fail_closed_default)}
                            <span className="ml-1 text-xs">(padrão restritivo)</span>
                          </span>
                        ) : (
                          <span className="italic text-ink-faint">não configurado</span>
                        )}
                      </td>
                      <td className="py-1 pr-3 text-ink-muted">
                        {STATUS_LABEL[p.status] ?? p.status}
                      </td>
                      <td className="py-1">
                        {cls === 'capability' ? (
                          <span className="text-xs text-ink-faint">somente leitura</span>
                        ) : cls === 'constitutional' ? (
                          // Constitucional NAO tem campo inline: abre fluxo governado.
                          <span className="text-xs">
                            {p.changeable_by_actor
                              ? 'abrir fluxo de política e ADR'
                              : 'solicitar ao dono'}
                          </span>
                        ) : p.changeable_by_actor && classeTemFormulario(p.class) ? (
                          // Botao de verdade, e nao texto que parece acao: ele
                          // abre o detalhe, onde vive o formulario com motivo.
                          <button
                            type="button"
                            onClick={() => setSelectedKey(p.parameter_key)}
                            className="rounded border border-line px-2 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
                          >
                            Alterar
                          </button>
                        ) : (
                          <span className="text-xs">
                            {p.changeable_by_actor ? 'alterar' : 'solicitar mudança'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      ))}

      {selected && (
        <section
          aria-labelledby="detalhe-parametro"
          className="rounded border border-line-strong bg-surface p-3"
        >
          <h2
            id="detalhe-parametro"
            className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
          >
            {selected.display_name}
          </h2>
          <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs text-ink-muted">Identificador de código</dt>
              <dd>{selected.parameter_key}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">Classe</dt>
              <dd>{CLASS_LABEL[selected.class]}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">Autoridade</dt>
              <dd>{selected.owner}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">Unidade</dt>
              <dd>{selected.unit ?? 'sem unidade'}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-ink-muted">Descrição</dt>
              <dd>{selected.description}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-ink-muted">Origem na especificação</dt>
              <dd className="text-ink-muted">{selected.spec_ref}</dd>
            </div>
            {selected.scheduled_change ? (
              <div className="sm:col-span-2">
                <dt className="text-xs text-ink-muted">Mudança marcada</dt>
                {/*
                  O valor agendado NAO substitui o vigente na tela: ele entra
                  como campo proprio, dizendo a data. Quem opera precisa ver que
                  o pedido dele existe, senao marca de novo achando que se
                  perdeu; e precisa ver que ainda nao vale, senao age sobre um
                  numero que nao esta no ar.
                */}
                <dd>
                  {selected.secret
                    ? 'valor não exibido (chave secreta)'
                    : JSON.stringify(selected.scheduled_change.value)}
                  <span className="text-ink-muted">
                    {' '}
                    entra em vigor em {formatInstant(selected.scheduled_change.effective_from)}, na
                    versão {selected.scheduled_change.version}
                  </span>
                </dd>
              </div>
            ) : null}
          </dl>
          {selected.changeable_by_actor && classeTemFormulario(selected.class) ? (
            <FormularioDeMudanca parametro={selected} onAplicado={() => onRecarregar?.()} />
          ) : null}

          <button
            type="button"
            onClick={() => setSelectedKey(null)}
            className="mt-3 rounded border border-line px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Fechar detalhe
          </button>
        </section>
      )}
    </div>
  );
}
